import { Box, GridItem, InputElement } from '@chakra-ui/react';
import {
  InvoiceStatusEnum,
  InvoiceSummaryResp,
  MassCreditInvoiceFormInput,
} from '@monetize/types/app';
import {
  formatCurrency,
  getCurrencyDecimals,
  getCurrencySymbol,
  toDateShort,
} from '@monetize/utils/core';
import { Fragment } from 'react';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import {
  MCustomNumberInput,
  MFormField,
  MText,
  MTooltip,
} from '../../../../components/Monetize';
import { ItemWithLinkInNewWindow } from '../../../../components/Monetize/ItemWithLinkInNewWindow';
import { MEntityStatusBadge } from '../../../../components/Monetize/MEntityStatusBadge';
import { InvoiceStatusEnumDisplay } from '../../../../constants/invoice';
import {
  getAccountDetailRoute,
  getInvoiceDetailRoute,
} from '../../../../constants/routes';

interface MassCreditModalFormRowProps {
  index: number;
  row: InvoiceSummaryResp;
  isLoading: boolean;
  hasSubmitted: boolean;
  isLastRow: boolean;
  control: Control<MassCreditInvoiceFormInput>;
  errors: FieldErrors<MassCreditInvoiceFormInput>;
  setValue: UseFormSetValue<MassCreditInvoiceFormInput>;
  watch: UseFormWatch<MassCreditInvoiceFormInput>;
}

export const MassCreditModalFormRow = ({
  index,
  row,
  isLoading,
  isLastRow,
  control,
  errors,
  hasSubmitted,
  watch,
}: MassCreditModalFormRowProps) => {
  const value = watch(`rows.${index}.amount`);
  const amountDue = row.amountDue - (Number(value) || 0);
  const currencyDecimals = getCurrencyDecimals(row.currency);

  return (
    <Fragment>
      <GridItem>
        <ItemWithLinkInNewWindow url={getInvoiceDetailRoute(row.id)}>
          <MText>{row.invoiceNumber || row.id}</MText>
        </ItemWithLinkInNewWindow>
        <MText fontSize="sm">
          {<MText fontSize="xs">Due {toDateShort(row.dueDate)}</MText>}
        </MText>
      </GridItem>
      <GridItem>
        <ItemWithLinkInNewWindow url={getAccountDetailRoute(row.account.id)}>
          <MText lineClamp={2}>{row.account.accountName}</MText>
        </ItemWithLinkInNewWindow>
        <MText fontSize="xs">{row.account.id}</MText>
      </GridItem>
      <GridItem alignContent="center">
        <MText textAlign="right">
          {formatCurrency(row.amount, {
            currency: row.currency,
          })}
        </MText>
      </GridItem>
      <GridItem alignContent="center">
        <MText textAlign="right">
          {formatCurrency(row.amountDue, {
            currency: row.currency,
          })}
        </MText>
      </GridItem>
      <GridItem>
        <MFormField error={errors.rows?.[index]?.amount}>
          <Controller
            name={`rows.${index}.amount`}
            control={control}
            render={({ field: { onChange, ...rest } }) => (
              <Box position="relative">
                <InputElement placement="start" w="4" pl="2">
                  {getCurrencySymbol(row.currency)}
                </InputElement>
                <MCustomNumberInput
                  inputMode="decimal"
                  precision={currencyDecimals}
                  placeholder={(0).toFixed(currencyDecimals)}
                  isReadOnly={isLoading || hasSubmitted}
                  isDisabled={isLoading || hasSubmitted}
                  styleProps={{
                    pl: 5,
                    pr: 2,
                    textAlign: 'right',
                  }}
                  onChange={(value) => onChange(value)}
                  {...rest}
                />
              </Box>
            )}
          />
        </MFormField>
      </GridItem>
      <GridItem alignContent="center">
        <MText textAlign="right">
          {formatCurrency(amountDue, { currency: row.currency })}
        </MText>
      </GridItem>
      <GridItem alignContent="center" justifySelf="end">
        <NewInvoiceStatusOrError amount={Number(value)} amountDue={amountDue} />
      </GridItem>
      {!isLastRow && (
        <GridItem
          gridColumn="1 / -1"
          height="1px"
          backgroundColor="tGray.back"
        />
      )}
    </Fragment>
  );
};

const NewInvoiceStatusOrError = ({
  amount,
  amountDue,
}: {
  amount: number;
  amountDue: number;
}) => {
  let toolTipText = '';
  if (Number(amount) === 0) {
    toolTipText = 'The amount must be greater than 0';
  } else if (amountDue < 0) {
    toolTipText = 'The applied amount cannot exceed the amount due';
  }
  if (toolTipText) {
    return (
      <MTooltip label={toolTipText}>
        <MText
          role="cell"
          color="tRed.base"
          alignSelf="center"
          justifySelf="flex-end"
        >
          Error
        </MText>
      </MTooltip>
    );
  }
  return (
    <MEntityStatusBadge
      role="cell"
      status={
        amountDue === 0
          ? InvoiceStatusEnumDisplay[InvoiceStatusEnum.PAID]
          : InvoiceStatusEnumDisplay[InvoiceStatusEnum.UNPAID]
      }
      alignSelf="center"
      justifySelf="flex-end"
    />
  );
};
